import Vue from 'vue';
import drivingPracticeExamService from '@/api/driving-practice-exam-service';
import eventBus, { OPEN_SNACKBAR } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';
import { updateArrayItem } from '@/util/array';

const state = {
  drivingPracticeExamParticipants: [],
  editedDrivingPracticeExamParticipant: {},
  drivingPracticeExamParticipantValidationErrors: {},
};

const getters = {
  examStatusesMap() {
    return {
      failed: {
        text: i18n.t('exam_statuses.failed'),
        value: 'failed',
        color: '#FF5252',
        tooltip: i18n.t('practice_exam_failed'),
      },
      pending: {
        text: i18n.t('exam_statuses.pending'),
        value: 'pending',
        color: '#FDD835',
        tooltip: i18n.t('practice_exam_needs_passed'),
      },
      passed: {
        text: i18n.t('exam_statuses.passed'),
        value: 'passed',
        color: '#00E676',
        tooltip: i18n.t('practice_exam_passed'),
      },
    };
  },

  examStatuses(state, getters) {
    return Object.values(getters.examStatusesMap);
  },
};

const mutations = {
  SET_DRIVING_PRACTICE_EXAM_PARTICIPANTS(state, data) {
    state.drivingPracticeExamParticipants = data;
  },

  SET_EDITED_DRIVING_PRACTICE_EXAM_PARTICIPANT(state, participant) {
    state.drivingPracticeExamParticipantValidationErrors = {};
    state.editedDrivingPracticeExamParticipant = JSON.parse(JSON.stringify(participant));
  },

  SET_DRIVING_PRACTICE_EXAM_PARTICIPANT_VALIDATION_ERRORS(state, validationErrors) {
    state.drivingPracticeExamParticipantValidationErrors = validationErrors;
  },

  CLEAR_DRIVING_PRACTICE_EXAM_PARTICIPANT_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.drivingPracticeExamParticipantValidationErrors, field);
  },

  UPDATE_DRIVING_PRACTICE_EXAM_PARTICIPANT(state, participant) {
    state.drivingPracticeExamParticipants = updateArrayItem(
      state.drivingPracticeExamParticipants,
      participant,
    );
  },
};

const actions = {
  fetchDrivingPracticeExamParticipants({ commit }, examId) {
    return drivingPracticeExamService.getParticipants(examId).then((res) => {
      commit('SET_DRIVING_PRACTICE_EXAM_PARTICIPANTS', res.data);
      return res.data;
    });
  },

  editDrivingPracticeExamParticipant({ state, commit }, participantId) {
    const examParticipant = state.drivingPracticeExamParticipants?.find(
      (p) => p.id === participantId,
    );
    if (examParticipant) {
      commit('SET_EDITED_DRIVING_PRACTICE_EXAM_PARTICIPANT', examParticipant);
      return Promise.resolve(examParticipant);
    }
    return drivingPracticeExamService.getParticipant(participantId).then((res) => {
      commit('SET_EDITED_DRIVING_PRACTICE_EXAM_PARTICIPANT', res.data);
      return res.data;
    });
  },

  updateDrivingPracticeExamParticipant({ commit }, participant) {
    return drivingPracticeExamService
      .updateParticipant(participant)
      .then((res) => {
        commit('UPDATE_DRIVING_PRACTICE_EXAM_PARTICIPANT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('exam_results_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_DRIVING_PRACTICE_EXAM_PARTICIPANT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
